import { ReactNode } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface SelectIconOption {
  value: string;
  label: string;
  /** Descripción corta bajo el nombre (ej. "Sistemas fotovoltaicos para techo o piso"). */
  sub?: string;
  icon: ReactNode;
}

interface Props {
  value: string;
  options: SelectIconOption[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  testId: string;
  /** Campo cerrado del alto normal y solo con el nombre (garantías de Pymio); la lista sigue con ícono y descripción. */
  compact?: boolean;
}

/** Fila de una opción (`Fila` del `IconSelect`): ícono en círculo de marca, nombre y descripción. */
const OptionRow = ({ option }: { option: SelectIconOption }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, width: "100%" }}>
    <Box
      component="span"
      sx={(theme) => ({
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "primary.base",
        color: "primary.main",
        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
        "& > svg": { fontSize: 20 },
      })}
    >
      {option.icon}
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: "primary.dark" }}>
        {option.label}
      </Typography>
      {option.sub && (
        <Typography variant="body3" noWrap color="text.secondary">
          {option.sub}
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * `IconSelect` del diseño: select amplio con la misma fila (ícono, nombre y descripción) en el campo y en la lista.
 * Parte del spec de `lendia_input` y solo cambia el alto, que crece con la fila.
 */
export const TextFieldSelectIcon = ({ value, options, onChange, onBlur, disabled, testId, compact }: Props) => (
  <TextField
    autoComplete="off"
    size="lendia_input"
    select
    fullWidth
    value={value}
    onBlur={onBlur}
    onChange={(event) => onChange(event.target.value)}
    disabled={disabled}
    inputProps={{ "data-testid": testId }}
    SelectProps={{
      displayEmpty: true,
      renderValue: (selected) => {
        const option = options.find((item) => item.value === selected);
        if (!option) {
          return (
            <Typography variant="body2" color="tokens.textTertiary">
              {compact ? "Selecciona…" : "Selecciona"}
            </Typography>
          );
        }
        return compact ? option.label : <OptionRow option={option} />;
      },
    }}
    sx={
      compact
        ? undefined
        : {
            "& .MuiOutlinedInput-root": { height: "auto", minHeight: 48 },
            "& .MuiOutlinedInput-input": { py: 1.5 },
          }
    }
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value} sx={{ py: 1.5 }}>
        <OptionRow option={option} />
      </MenuItem>
    ))}
  </TextField>
);
